import React, { useState, useEffect } from 'react';
import { ChevronLeft, Video, Download, RefreshCw, Sliders, CreditCard, Loader } from 'lucide-react';

const ShortMovieScreen = ({ onBack }) => {
  // API URL for your Cloudflare Worker
  const API_URL = 'https://veo2-worker.kgu-ishizaka.workers.dev';
  
  // Form state
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [numFrames, setNumFrames] = useState(25);
  const [fps, setFps] = useState(6);
  const [width, setWidth] = useState(1024);
  const [height, setHeight] = useState(576);
  const [guidanceScale, setGuidanceScale] = useState(9.0);
  const [seed, setSeed] = useState('');
  
  // UI state
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [status, setStatus] = useState('');
  const [isPolling, setIsPolling] = useState(false);
  const [generationHistory, setGenerationHistory] = useState([]);

  // Video resolution options
  const resolutionOptions = [
    { id: '512x768', name: '縦長 (2:3)' },
    { id: '1024x576', name: '横長 (16:9)' },
    { id: '768x768', name: '正方形 (1:1)' }
  ];

  // Quality/Style options
  const qualityOptions = [
    { id: 'standard', name: '標準' },
    { id: 'high', name: '高品質' }
  ];

  // Handle video generation
  const generateVideo = async () => {
    if (!prompt) return;
    
    try {
      setError(null);
      setIsGenerating(true);
      setVideoUrl(null);
      setStatus('初期化中...');
      
      // Prepare request data
      const requestData = {
        prompt: prompt.trim(),
        negative_prompt: negativePrompt.trim(),
        num_frames: parseInt(numFrames),
        fps: parseInt(fps),
        width: parseInt(width),
        height: parseInt(height),
        guidance_scale: parseFloat(guidanceScale)
      };
      
      // Add seed if provided
      if (seed.trim() !== '') {
        requestData.seed = parseInt(seed.trim());
      }
      
      // Make API call to worker
      const response = await fetch(`${API_URL}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '動画生成に失敗しました');
      }
      
      const data = await response.json();
      
      if (data.success && data.prediction_id) {
        // Start polling for results
        setIsPolling(true);
        setStatus('処理中...');
        pollForResults(data.prediction_id);
      }
    } catch (err) {
      setError(err.message);
      setIsGenerating(false);
    }
  };
  
  // Poll for generation results
  const pollForResults = async (predictionId) => {
    try {
      const response = await fetch(`${API_URL}/check?id=${predictionId}`);
      
      if (!response.ok) {
        throw new Error('生成状態の確認に失敗しました');
      }
      
      const data = await response.json();
      
      // Update status based on prediction status
      if (data.status === 'succeeded') {
        setStatus('完了！');
        setIsPolling(false);
        setIsGenerating(false);
        
        // If we have output, set the video URL
        if (data.output) {
          const url = Array.isArray(data.output) ? data.output[0] : data.output;
          setVideoUrl(url);
          
          // Add to history
          setGenerationHistory(prev => [{
            url,
            prompt,
            seed: seed || null,
            timestamp: new Date().toISOString()
          }, ...prev].slice(0, 6));
        }
      } else if (data.status === 'failed') {
        throw new Error(data.error || '生成に失敗しました');
      } else {
        // Continue polling
        setStatus(`処理中... (${data.status})`);
        setTimeout(() => pollForResults(predictionId), 3000);
      }
    } catch (err) {
      setError(err.message);
      setIsPolling(false);
      setIsGenerating(false);
    }
  };
  
  // Download video
  const handleSaveVideo = () => {
    if (!videoUrl) return;
    
    // Create filename
    const fileName = `veo2-video-${new Date().getTime()}.mp4`;
    
    // Create download link and trigger click
    const link = document.createElement('a');
    link.href = videoUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle resolution change
  const handleResolutionChange = (resolution) => {
    const [w, h] = resolution.split('x').map(Number);
    setWidth(w);
    setHeight(h);
  };

  return (
    <div className="bg-gray-100">
      <div className="bg-white px-4 py-3 flex items-center shadow">
        <button 
          onClick={onBack}
          className="text-blue-500 mr-3"
        >
          <ChevronLeft size={24} />
        </button>
        <h2 className="text-lg font-bold">AI動画生成</h2>
      </div>
      
      <div className="p-4">
        {/* プロンプト入力エリア */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700">プロンプト</label>
          <textarea
            className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
            placeholder="生成したい動画の詳細な説明を入力してください（例：青い空を背景に、花畑の中を歩く少女）"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
          />
        </div>

        {/* 基本パラメータ */}
        <div className="mb-4 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">解像度</label>
            <select
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              value={`${width}x${height}`}
              onChange={(e) => handleResolutionChange(e.target.value)}
            >
              {resolutionOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">FPS</label>
            <select
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              value={fps}
              onChange={(e) => setFps(parseInt(e.target.value))}
            >
              <option value="6">6 FPS (標準)</option>
              <option value="12">12 FPS</option>
              <option value="24">24 FPS (滑らか)</option>
            </select>
          </div>
        </div>

        {/* 詳細設定トグル */}
        <button 
          className="w-full flex items-center justify-center py-2 text-blue-500 text-sm font-medium mb-4"
          onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
        >
          詳細設定
          <Sliders size={14} className="ml-1" />
        </button>

        {/* 詳細設定パネル */}
        {isAdvancedOpen && (
          <div className="mb-4 bg-white rounded-lg shadow-md p-4">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-gray-700">ネガティブプロンプト</label>
              <textarea
                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                placeholder="生成したくない要素を指定（例：ぼやけた映像、低品質、歪み）"
                value={negativePrompt}
                onChange={(e) => setNegativePrompt(e.target.value)}
                rows={2}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-gray-700">フレーム数</label>
              <div className="flex items-center">
                <input
                  type="range"
                  min="15"
                  max="50"
                  value={numFrames}
                  onChange={(e) => setNumFrames(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="ml-2 w-8 text-sm text-gray-700">{numFrames}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>短い</span>
                <span>長い</span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-gray-700">プロンプト忠実度</label>
              <div className="flex items-center">
                <input
                  type="range"
                  min="1"
                  max="15"
                  step="0.5"
                  value={guidanceScale}
                  onChange={(e) => setGuidanceScale(parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="ml-2 w-8 text-sm text-gray-700">{guidanceScale}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>自由</span>
                <span>忠実</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">シード値</label>
              <input
                type="text"
                placeholder="空白でランダム生成"
                value={seed}
                onChange={(e) => setSeed(e.target.value.replace(/[^0-9]/g, ''))}
                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              <p className="text-xs text-gray-500 mt-1">同じシード値を使うと似た結果が得られます</p>
            </div>
          </div>
        )}
        
        {/* 生成ボタンと使用クレジット */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center text-gray-500 text-sm">
              <CreditCard size={16} className="mr-1" />
              <span>使用クレジット: {numFrames > 30 ? 2 : 1}</span>
            </div>
            {numFrames > 30 && (
              <div className="text-amber-600 text-sm font-medium">長い動画は2倍のクレジットを使用</div>
            )}
          </div>
          
          <button
            className={`w-full py-3 rounded-lg font-medium text-white ${
              isGenerating || !prompt 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
            }`}
            onClick={generateVideo}
            disabled={isGenerating || !prompt}
          >
            {isGenerating ? (
              <span className="flex items-center justify-center">
                <Loader size={20} className="animate-spin mr-2" />
                {status}
              </span>
            ) : '動画を生成'}
          </button>
        </div>

        {/* エラーメッセージ */}
        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* 生成結果 */}
        {videoUrl && (
          <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-medium">生成結果</h3>
              <div className="flex space-x-2">
                <button 
                  className="text-blue-500 p-1 hover:bg-blue-50 rounded-md transition-colors"
                  onClick={generateVideo}
                  title="再生成"
                  disabled={isGenerating}
                >
                  <RefreshCw size={18} className={isGenerating ? "animate-spin" : ""} />
                </button>
                <button 
                  className="text-blue-500 p-1 hover:bg-blue-50 rounded-md transition-colors"
                  onClick={handleSaveVideo}
                  title="ダウンロード"
                >
                  <Download size={18} />
                </button>
              </div>
            </div>
            
            <div className="rounded-lg overflow-hidden border border-gray-200 bg-black">
              <video 
                src={videoUrl} 
                controls
                autoPlay
                loop
                className="w-full h-auto" 
              />
            </div>
            
            <div className="mt-3 text-sm text-gray-600">
              <p className="line-clamp-2">{prompt}</p>
              {seed && <p className="mt-1 text-xs text-gray-500">シード値: {seed}</p>}
            </div>
          </div>
        )}

        {/* 生成履歴 */}
        {generationHistory.length > 0 && (
          <div className="mb-4 bg-white rounded-lg shadow-lg p-4">
            <h3 className="font-medium mb-3">最近の生成履歴</h3>
            <div className="grid grid-cols-3 gap-2">
              {generationHistory.map((item, index) => (
                <div 
                  key={index} 
                  className="aspect-video rounded-md overflow-hidden relative cursor-pointer hover:opacity-80 transition-opacity bg-black"
                  onClick={() => {
                    setVideoUrl(item.url);
                    setPrompt(item.prompt);
                    if (item.seed) setSeed(item.seed.toString());
                  }}
                >
                  <video 
                    src={item.url}
                    className="w-full h-full object-cover"
                    muted
                    onMouseOver={(e) => e.target.play()}
                    onMouseOut={(e) => {e.target.pause(); e.target.currentTime = 0;}}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* 情報パネル */}
        <div className="bg-white rounded-lg shadow-lg p-4">
          <h3 className="font-medium mb-3">Google Veo 2について</h3>
          <p className="text-sm text-gray-600 mb-2">
            Google Veo 2はGoogleが開発した最新のAI動画生成モデルです。テキストプロンプトから高品質な短い動画を生成できます。
          </p>
          <p className="text-sm text-gray-600">
            最高の結果を得るためのヒント：
          </p>
          <ul className="text-sm text-gray-600 list-disc pl-5 mt-1">
            <li>詳細で具体的なプロンプトを使用する</li>
            <li>シーン、アクション、雰囲気を明確に記述する</li>
            <li>ネガティブプロンプトで不要な要素を除外する</li>
            <li>同じシード値を使って反復的に改善する</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ShortMovieScreen;